import {
    Alignment, Api, DateFormat, DatePart, DateUtil, Logger, LogManager,
    MixinUtil, ModelRow, Navigation, StringUtil, UrlUtil
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ClickEvent, Component, ContextMenuCreationEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, DataSourceRemoteDataChangeEvent, Image, Label, Layout, Panel, SlideoutDecorator, Table, TableRow, TableRowDisplayEvent, Textbox } from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { getLateImageTooltip, getTimeDiff } from "@mcleod/powerbroker/src/BrokeragePlanningUtil";
import { DriverEventSlideout } from "../../DriverEventSlideout";
import { ManifestMovementQuickInfo } from "../../ManifestMovementQuickInfo";
import { EquipmentAssignments } from "../../asset/EquipmentAssignments";
import { AppointmentUtil, ShipmentStatusSlideout } from "../../index";
import { MobileCommMessages } from "../../mobilecomm/MobileCommMessages";
import { EditEquipment } from "../EditEquipment";
import { FilterPanel, SecondaryFilterProps } from "../FilterPanel";
import { OrderPlanningLayout } from "../OrderPlanningLayout";
import { OrderPlanningSecondaryFilter } from "../OrderPlanningSecondaryFilter";
import { OrderPlanningToolPanel, Tools } from "../OrderPlanningToolPanel";
import { PlanningUtil } from "../PlanningUtil";
import { AutogenLayoutOrderPlanningEquipment } from "./autogen/AutogenLayoutOrderPlanningEquipment";
import { LateRouteViewSlideout } from "../../asset/LateRouteViewSlideout";

const log = LogManager.getLogger("lme.dispatch.planning.opsmgmt.OrderPlanningEquipment");

export class OrderPlanningEquipment extends AutogenLayoutOrderPlanningEquipment {
    private shipmentStatusError: boolean;
    private shipmentStatusCount: number;
    private dispatchByDriver: boolean;
    private enforceRegulatory: boolean;
    private equipFilterPanel: FilterPanel;
    private dcMaxTrailers = getDispatchControlValue("maximum_trailers");
    private equipSecondaryFilters: SecondaryFilterProps[] = [
        OrderPlanningSecondaryFilter.FILTER_DISPATCH_BY_UNASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_DISPATCH_BY_ASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_TRACKING_LATE,
        OrderPlanningSecondaryFilter.FILTER_MC_MESSAGES,
        OrderPlanningSecondaryFilter.FILTER_LATE,
        OrderPlanningSecondaryFilter.FILTER_SHIPMENT_STATUS_ERRORS,
        OrderPlanningSecondaryFilter.FILTER_EQUIP_ARRIVED_AT_SHIPPER,
        OrderPlanningSecondaryFilter.FILTER_ARRIVED_AT_CONSIGNEE,
        OrderPlanningSecondaryFilter.FILTER_TEMP_WARNINGS,
        OrderPlanningSecondaryFilter.FILTER_UNSEATED_TRACTORS,
        OrderPlanningSecondaryFilter.FILTER_FREE_TIME_STARTED,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_CHARGING,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_OCCURRED,
        OrderPlanningSecondaryFilter.FILTER_DETENTION_CLOSED,
    ];

    getThisAsLayout(): Layout {
        return this;
    }

    getTable(): Table {
        return this.tableEquipMoves;
    }

    getToolsPanel(): OrderPlanningToolPanel {
        return this.layoutOPEquipTools.toolPanel;
    }

    getLog(): Logger {
        return log;
    }

    override async onLoad() {
        this.dispatchByDriver = getDispatchControlValue("dispatch_by") === "D";
        this.enforceRegulatory = getDispatchControlBoolean("enforce_regulatory", true);
        this.setTitles();
        this.applyUrlParams();
        this.applyCommonTableSetup(this.tableEquipMoves);
        this.equipFilterPanel = new FilterPanel(this.equipSecondaryFilters, this.tableEquipMoves, this.panelEquipFilters);
        await this.layoutOPEquipTools.showButtons(Tools.EQUIP_MANAGE_ASSIGN, Tools.DISPATCH, Tools.YSPLIT, Tools.LOAD_MATCH,
            Tools.DRIVER_EVENT, Tools.VIEW_MOVEMENTS, Tools.ZMIT, Tools.CANCEL_SENT_LOAD, Tools.SEND_MESSAGE, Tools.SEAT_TRACTOR, Tools.FEASIBILITY);
        this.tableEquipMoves.determineDetailKeyValue = (tableRow) => tableRow.data.get("movement_id");
        PlanningUtil.showHideTrailerColumns(this.getTable())
        this.initTimer();
        if (this.isNested !== true)
            this.loadProfile();

        const columnDeadhead = this.tableEquipMoves.getColumnByCaption("Deadhead");
        const sortSelector = columnDeadhead.headingCell.getSortSelector();
        sortSelector.addClickListener(() => sortSelector.getFieldLabel().color = "success");
        sortSelector.getFieldLabel().color = "success";
        this.setupCustomRowComparator();
    }

    private setupCustomRowComparator() {
        this.sourceEquipMove.customRowComparator = ((newRow: ModelRow, oldRow: ModelRow) => {
            const eventSource = newRow.getString("source_type");
            if (newRow.getString("company_id") !== oldRow.getString("company_id"))
                return false;
            const isTractor = "T" === this.profile.getString("tractor_or_driver");
            if ("A" === eventSource)  {
                if (["A", "C"].includes(oldRow.getString("move_status")) && newRow.getString("movement_id") === oldRow.getString("movement_id"))
                    return true;
            }
            else if (!["A", "C"].includes(oldRow.getString("move_status")) && !isTractor && newRow.getString("driver1_id") === oldRow.getString("driver1_id"))
                return true;
            else if (!["A", "C"].includes(oldRow.getString("move_status")) && isTractor && newRow.getString("tractor") === oldRow.getString("tractor"))
                return true;

            return false;
        });
    }

    private setColumnHeaderTooltip(columnCaption: string, tooltip: string) {
        const column = this.tableEquipMoves.getColumnByCaption(columnCaption);
        if (column != null) {
            column.headingCell.getSortSelectorLabel().tooltip = tooltip;
        }
    }

    setTitles() {
        this.layoutPlanningTitle.majorTitle = this.isDisplayByDriver() ? "Drivers" : "Tractors";
        this.layoutPlanningTitle.minorTitle = "Planning";
    }

    private isDisplayByDriver(): boolean {
        return this.profile?.get("tractor_or_driver") === "D"
    }

    override async search(): Promise<any> {
        const findNearProps = this.findNearProps;
        this.sourceEquipMove.search({profile_row: this.profile, ...findNearProps})
            .then((response) => {
                // Add after search logic here
                if (findNearProps) {
                    this.tableEquipMoves.clearSort();
                    this.tableEquipMoves.applySort("deadhead", "asc", true);
                }
                this.setSecondaryFilters();
            }).catch(error => log.debug("EquipMove search failed: %o", error));
    }

    applyUrlParams() {
        const params = UrlUtil.getPropsFromUrl(window.location.href);
        this.shipmentStatusError = params?.["shipmentStatusError"];
        this.shipmentStatusCount = parseInt(params?.["planningEquipmentCount"], 10) || 0;
    }

    private setSecondaryFilters() {
        if (this.shipmentStatusError) {
            this.equipFilterPanel.toggleFilterById(OrderPlanningSecondaryFilter.FILTER_SHIPMENT_STATUS_ERRORS.id);
            this.shipmentStatusError = false;
        }
    }

    sourceEquipMoveAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) return;
        this.sourceEquipMove.data.forEach(row => PlanningUtil.calculateReeferTempStatus(row));
        this.equipFilterPanel.syncFilterVisibility(this.sourceEquipMove.data);
    }

    tableEquipMovesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;

        tableRow.contextMenuItems = () => this.getToolsPanel().getContextMenuItems(tableRow);
        tableRow.addContextMenuCreationListener((event: ContextMenuCreationEvent) => {
            this.getToolsPanel().contextMenuCreationEvent(event);
        });

        const hasDriver2 = !modelRow.isNull("driver2_id");
        this.setEquipEquipment(tableRow);
        this.setOrderId(tableRow, modelRow);
        this.setEvents(tableRow, hasDriver2);
        this.setPreassignInfo(tableRow, modelRow);
        this.setServiceIncidentsInfo(tableRow, modelRow);
        this.setDeadInfo(tableRow, modelRow);
        this.setEquipTimes(tableRow);
        this.displayPackageErrorImage(tableRow);
        PlanningUtil.setPlanningCommentsIcon(tableRow, modelRow);
        PlanningUtil.setUpMcMessages(tableRow, modelRow);
        PlanningUtil.setupDriverStatus(tableRow, modelRow, hasDriver2);
        PlanningUtil.setupNextStopLateStatusPanel(tableRow);
        PlanningUtil.setupDriverPta(tableRow, 1);
        PlanningUtil.setupYsplitIcons(tableRow, modelRow);
        if (hasDriver2)
            PlanningUtil.setupDriverPta(tableRow, 2);
        PlanningUtil.displayDetentionIcon(tableRow);
        this.setupNextStop(tableRow, modelRow);
        this.setArrivedBadge(tableRow, modelRow);
        this.doEveryMinuteRow(tableRow);

        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        if (manifestIdLabel != null) {
            const manifestId = modelRow.get("manifest_id");
            const movementId = modelRow.get("movement_id");
            manifestIdLabel.addUnderlineWhenMouseOver();
            ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(manifestIdLabel, movementId, manifestId);
        }
        tableRow.forEveryChildComponent(comp => {
            PlanningUtil.setPlanningTooltipContent(comp, modelRow);
        });

        OrderPlanningEquipment.checkScheduledLate(modelRow, tableRow, "origin");
        OrderPlanningEquipment.checkScheduledLate(modelRow, tableRow, "next_stop");
        OrderPlanningEquipment.checkScheduledLate(modelRow, tableRow, "destination");

        PlanningUtil.determineOrderNumberVisibility(tableRow, modelRow);
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const source = event.target as DataSource;
        const changedRow = event.change.data as ModelRow;
        const index = source.findRowInData(changedRow);
        const currentRow = index >= 0 ? source.data[index] : null;

        this.testUpdateFreshness(event, currentRow);
        if (event.defaultPrevented !== true) {
            PlanningUtil.calculateReeferTempStatus(changedRow);
            PlanningUtil.syncOrderPlanningComments(currentRow, source, this.tableEquipMoves);
            if (this.findNearProps != null && currentRow != null) {
                changedRow.set("deadhead", currentRow.get("deadhead"));
            }
            this.adjustEventType(event, currentRow);
        }
    }

    private initTimer() {
        const callback = () => {
            for (const tableRow of this.tableEquipMoves.rows) {
                if (!tableRow.populatedDOM)
                    return;
                this.doEveryMinuteRow(tableRow);
            }
        };
        this.startMinuteTimer(callback);
    }

    private doEveryMinuteRow(tableRow: TableRow) {
        // call your equip update function(s) here
        PlanningUtil.checkScheduledLate(tableRow, "next_stop");
    }

    private setArrivedBadge(tableRow: TableRow, modelRow: ModelRow) {
        const arrived = !modelRow.isNull("next_stop_actual_arrival") && modelRow.isNull("next_stop_actual_departure")
        const arrivedBadge = tableRow.findComponentById("labelArrived") as Label;
        arrivedBadge.visible = arrived;

        const distanceToNextStop = tableRow.findComponentById("labelDistance") as Label;
        distanceToNextStop.visible = !arrived;
    }

    private setEquipEquipment(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        this.setReeferIcon(tableRow, modelRow);
        PlanningUtil.setEquipIcons(tableRow, modelRow, this.enforceRegulatory);

        // Driver 1
        PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageD1Alert", "driver1_msg_count",
            "driver1_msg_urgent", { driverId: modelRow.get("driver1_id") });

        // Driver 2
        PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageD2Alert", "driver2_msg_count",
            "driver2_msg_urgent", { driverId: modelRow.get("driver2_id") });

        // Tractor and Trailer 1
        PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT1Alert", "t1_msg_count",
            "t1_msg_urgent", { tractorId: modelRow.get("tractor"), trailerId: modelRow.get("trailer") });

        if (this.dcMaxTrailers >= 2) {
            // Trailer 2
            PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT2Alert", "trailer2_msg_count",
                "trailer2_msg_urgent", { trailerId: modelRow.get("trailer2") });
            if (this.dcMaxTrailers >= 3) {
                // Trailer 3
                PlanningUtil.displayEquipAlerts(tableRow, modelRow, "imageT3Alert", "trailer3_msg_count",
                    "trailer3_msg_urgent", { trailerId: modelRow.get("trailer3") });
            }
        }

        PlanningUtil.setDroppedTrailerFont(tableRow, modelRow, this.dcMaxTrailers);
    }

    private setOrderId(tableRow: TableRow, modelRow: ModelRow) {
        const label = tableRow.findComponentById("labelEquipOrderId") as Label;
        if (label != null) {
            label.visible = modelRow.isNull("manifest_id");
            if (!modelRow.isNull("order_id") && !modelRow.getBoolean("emptied", false)) {
                this.styleLinkLabel(label);
            } else {
                label.tooltip = null;
                const labelEmptyOrder = tableRow.findComponentById("labelEmptyOrder") as Label;
                labelEmptyOrder?.addUnderlineWhenMouseOver();
            }
        }
    }

    private getEquipProps(primary: boolean, noTrailer: boolean) {
        return {
            fontBold: primary,
            fontSize: primary ? "large" : "medium",
            rowBreak: primary,
            maxWidth: primary || noTrailer ? null : 120
        }
    }

    private setEvents(tableRow: TableRow, hasDriver2: boolean) {
        const data = tableRow.data as ModelRow;
        const setFields = (driver: number) => {
            const driverNum = driver.toString();
            if (!data.isNull(`driver${driverNum}_closest_event`)) {
                const eventsPanel = tableRow.findComponentById(`panelD${driverNum}Events`) as Panel;
                eventsPanel.visible = true;
            }
            if (!data.isNull(`driver${driverNum}_next_home`)) {
                const nextHomeDate = data.getDate(`driver${driverNum}_next_home`);
                const today = new Date();
                const diff = nextHomeDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
                const dayOutTextbox = tableRow.findComponentById(`textboxD${driverNum}DaysOut`) as Textbox;
                const hourSuffix = diffDays != 1 ? "Days" : "Day";
                dayOutTextbox.text = diffDays < -30 ? "--" : `${diffDays} ${hourSuffix} Out`;
            }
        }
        setFields(1);
        if (hasDriver2)
            setFields(2);
    }
    private setServiceIncidentsInfo(tableRow: TableRow, modelRow: ModelRow) {
        const imageServiceFail = tableRow.findComponentById("imageEquipServiceIncidents") as Image;
        if (imageServiceFail != null) {
            CommonTooltips.setTooltipContentLayout(imageServiceFail, null, "lme/dispatch/ServiceIncidentQuickInfo", { maxWidth: 800, maxHeight: 600 },{ movement_id: modelRow.get("movement_id"), order_id: modelRow.get("order_id"), "status": "A" });
        }
    }

    private setPreassignInfo(tableRow: TableRow, modelRow: ModelRow) {
        const equipField = this.dispatchByDriver ? "driver1_id" : "tractor";
        const preassigned = modelRow.get("dispatchby_preassign_count") > 0;
        const moveStatus = modelRow.get("move_status");

        const labelPreassignCount = tableRow.findComponentById(`labelPreassignCount`) as Label;
        labelPreassignCount.visible = preassigned;
        if (preassigned) {
            CommonTooltips.setTooltipContentLayout(
                labelPreassignCount, null, "lme/dispatch/planning/PreassignOrderQuickInfoTable",
                {width: 800},
                {"equipment_type": this.dispatchByDriver ? "D" : "T", "equipment_id": modelRow.get(equipField)}
            );
            labelPreassignCount.tooltipDynamicOptions = { position: Alignment.TOP };
        }
        this.setLocationFields(tableRow, modelRow, preassigned, moveStatus);
        this.setTimeFields(tableRow, modelRow, preassigned, moveStatus);
    }

    private setDeadInfo(tableRow: TableRow, modelRow: ModelRow) {
        if (modelRow.isNull("deadhead")) return;
        const labelDeadhead = tableRow.findComponentById("labelDeadhead")
        const getMethodDisplayValue = () => {
            switch (getDispatchControlValue("findnear_method")) {
                case "S":
                    return "Straight Line";
                case "M":
                    return "Move Miles";
                case "F":
                    return "Fuel Miles";
            }
        }

        CommonTooltips.setTooltipContentTextboxes(labelDeadhead, () => [
            {
                caption: "Deadhead From",
                text: modelRow.getString("deadhead_from_loc")
            },
            {
                caption: "Pulled From",
                text: modelRow.getString("deadhead_from")
            },
            {
                caption: "Distance Method",
                text: getMethodDisplayValue()
            }
        ]);
    }

    private setLocationFields(tableRow: TableRow, modelRow: ModelRow, preassigned: boolean, moveStatus: string) {
        const image = tableRow.findComponentById("imageStopIcon") as Image;
        const setStopIcon = (image: Image, imageName: ImageName, tooltip: string, visible: boolean) => {
            image.name = imageName;
            image.tooltip = tooltip;
            image.visible = visible;
        }

        switch (moveStatus) {
            case "C":
                setStopIcon(image, ImageName.loadCovered, "Covered", true);
                break;
            case "P":
                setStopIcon(image, ImageName.loadInProgress, "In Progress", true);
                break;
            case "D":
                setStopIcon(image, ImageName.loadDelivery, "Delivered", true);
                break;
            case "A":
                setStopIcon(image, ImageName.loadAvailable, "Available", true);
                break;
            default:
                setStopIcon(image, null, null, false);
        }
    }

    private setTimeFields(tableRow: TableRow, modelRow: ModelRow, preassigned: boolean, moveStatus: string) {
        const labelIdelText = tableRow.findComponentById("labelIdleText") as Label;
        const panelDestTimes = tableRow.findComponentById("panelDestTimes") as Panel;
        const deliveryTz2 = tableRow.findComponentById("labelEquipDestTz2") as Label;

        const dateText = DateUtil.formatDateTime(modelRow.getDateTime("destination_actual_departure", null), DateUtil.getDateFormat(DateFormat.DATE_TIME))
        labelIdelText.text = `${dateText} ${modelRow.get("destination_timezone")}`;

        labelIdelText.visible = "D" === moveStatus;
        panelDestTimes.visible = "D" !== moveStatus;
        if ("D" === moveStatus && modelRow.isNull("destination_actual_departure")) {
            const arrivalDateText = DateUtil.formatDateTime(modelRow.getDateTime("destination_actual_arrival", null), DateUtil.getDateFormat(DateFormat.DATE_TIME));
            labelIdelText.text = `${arrivalDateText} ${modelRow.get("destination_timezone")}`;
        }
        labelIdelText.color = preassigned ? "default" : "error";

        deliveryTz2.visible = !modelRow.isNull("destination_late");

        if (!modelRow.isNull("destination_late")) {
            this.displayArriveLateTime(tableRow, "labelEquipDestLateTime", "cp_dest_late",
                modelRow.getBoolean("cp_dest_sameday", false), "panelEquipDestLate");
        }

        this.displayArriveLateTime(tableRow, "labelFinalPreassignmentLateTime", "fp_dest_late",
            modelRow.getBoolean("fp_dest_sameday", false), "panelFinalPreassignmentLate");
    }

    private displayPackageErrorImage(tableRow: TableRow) {
        const imagePackageError = tableRow.findComponentById("imagePackageError") as Image;
        if (tableRow.data.getBoolean("has_shipment_error")) {
            imagePackageError.visible = true;
            CommonTooltips.setTooltipContentLayout(imagePackageError, null, "lme/datafusion/StatusErrorQuickInfo", null,
                {"parent_id": tableRow.data.get("status_id"), "parent_tablename": "edistatus"});
            imagePackageError.tooltipDynamicOptions = { position: Alignment.BOTTOM };
        } else {
            imagePackageError.visible = false;
        }
    }

    private setupNextStop(tableRow: TableRow, modelRow: ModelRow) {
        const labelETAToNextStop = tableRow.findComponentById("labelETAToNextStop") as Label;
        if (labelETAToNextStop != null) {
            labelETAToNextStop.visible = !modelRow.isNull("next_stop_eta_time");
        }
        this.setupNextStopWindow(tableRow, modelRow);
    }

    private setupNextStopWindow(tableRow: TableRow, modelRow: ModelRow) {
        const nextStopEarly: Date = modelRow.getDateTime("next_stop_early", null);
        const nextStopLate: Date = modelRow.getDateTime("next_stop_late", null);
        const nextStopEarlyF: string = DateUtil.formatDateTime(nextStopEarly, DateUtil.getUserDateTimeFormat());
        const nextStopLateF: string = DateUtil.formatDateTime(nextStopLate, DateUtil.getUserDateTimeFormat());
        const timezone: string = modelRow.getString("next_stop_timezone", "");
        let nextStopWindow: string = "";

        if (nextStopEarly && nextStopLate) {
            if (nextStopEarly.toDateString() === nextStopLate.toDateString()) {
                const lateTime = DateUtil.formatTime(nextStopLate, DateUtil.getUserTimeFormat());
                nextStopWindow = `${nextStopEarlyF} - ${lateTime} ${timezone}`;
            } else {
                nextStopWindow = `${nextStopEarlyF} - ${nextStopLateF} ${timezone}`;
            }
        } else if (nextStopEarly) {
            nextStopWindow = `${nextStopEarlyF} ${timezone}`;
        } else if (nextStopLate) {
            nextStopWindow = `${nextStopLateF} ${timezone}`;
        }

        const nextStopWindowLabel = tableRow.findComponentById("labelEquipNextWindow") as Label;
        nextStopWindowLabel.text = nextStopWindow;
    }

    imageDriverEventsOnClick(event: ClickEvent, driver: number) {
        const slideOut = new DriverEventSlideout();
        const data = TableRow.getContainingTableRow(event.target as Image).data;
        const driverNum = driver.toString();

        slideOut.showSlideout({
            equipmentId: data.get(`driver${driverNum}_id`),
            equipmentName: data.get(`driver${driverNum}_name`),
            driverMgrId: data.get(`driver${driverNum}_manager`),
            driverMgrName: data.get(`driver${driverNum}_manager_name`),
            ...(data.isNull(`driver${driverNum}_closest_event_id`) ? {} : {equipStatusId: data.get(`driver${driverNum}_closest_event_id`)}),
            doAfterSave: () => {}
        });
    }


    imageDriver1EventsOnClick(event: ClickEvent) {
        this.imageDriverEventsOnClick(event, 1)
    }

    imageDriver2EventsOnClick(event: ClickEvent) {
        this.imageDriverEventsOnClick(event, 2)
    }

    public static checkScheduledLate(modelRow: ModelRow, tableRow: TableRow, stop: "origin" | "next_stop" | "destination") {
        const panelDate = tableRow.findComponentById("panelNextStopWindow") as Panel;

        if (panelDate == null)
            return;

        const isOrigin = "origin" === stop;

        const apptTimeDiff = getApptTimeDiff(tableRow, stop, panelDate);
        const isApptLate = apptTimeDiff < 0;
        const isApptApproaching = apptTimeDiff != null;
        panelDate.tooltip = isApptLate ? `This movement's ${isOrigin ? "pickup" : "delivery"} time has passed` : isApptApproaching ? getLateImageTooltip(apptTimeDiff, isOrigin ? "Pickup" : "Deliver") : null;

        panelDate.components.forEach(component => {
            component.color = isApptLate ? "error" : isApptApproaching ? "warning" : ""
            component.fontBold = isApptLate || isApptApproaching;
        });

        function getApptTimeDiff(tableRow: TableRow, stop: "origin" | "next_stop" | "destination", comp: Component): number {
            const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
            const isOrigin = "origin" === stop;
            const warning = isOrigin ? dispatchControl.pickup_warning : dispatchControl.delivery_warning;
            const diff = getTimeDiff(new Date(PlanningUtil.getStopTime(tableRow?.data, stop)));

            if (diff == null || (diff > (warning * 60 * 60 * 1000)) || (tableRow.data.get(`${stop}_actual_arrival`) != null)) return null;
            return diff;
        }
    }

    private setEquipTimes(tableRow: TableRow) {
        AppointmentUtil.setAppointmentImageForTableRow(tableRow, "imageEquipDestAppointment", "destination_appt_required", "destination_appt_confirmed")
    }

    static displayNewDriverDays(labelNewDriver: Label, hireDate: Date) {
        const dateDiff = DateUtil.dateDiff(DatePart.DAY, new Date(), hireDate);
        const newDriverDays1 = getDispatchControlValue("new_driver_days1");
        const newDriverDays2 = getDispatchControlValue("new_driver_days2");
        labelNewDriver.text = dateDiff > 0 ? dateDiff <= newDriverDays1 ? newDriverDays1 : dateDiff <= newDriverDays2 ? newDriverDays2 : "" : "";
        labelNewDriver.tooltip = !StringUtil.isEmptyString(labelNewDriver.text) ? `The driver's hire date was ${dateDiff} days ago.` : "";
        labelNewDriver.visible = !StringUtil.isEmptyString(labelNewDriver.text);
    }

    labelPreassignOnClick(event: ClickEvent) {
        const modelRow = TableRow.getContainingTableRow(event.target as Label).data as ModelRow;
        if (modelRow == null)
            return;

        const delivered = "D" === modelRow.get("move_status", "");
        const moveId = modelRow.get(delivered ? "np_move_id" : "movement_id");

        const button = event.target as Button;
        button.busy = true;
        const dispatchBy = getDispatchControlValue("dispatch_by");
        const equipmentId: string = dispatchBy == "T" ? modelRow.get("tractor") : modelRow.get("driver1_id");
        EquipmentAssignments.showSlideout({
            equipmentId: equipmentId,
            tractorId: modelRow.get("tractor"),
            movementId: moveId
        });
        button.busy = false;
    }

    hosOnClick(event: ClickEvent) {
        PlanningUtil.showHoursOfServiceSlideout(event, "driver1_id");
    }

    hosOnClickDriver2(event: ClickEvent) {
        PlanningUtil.showHoursOfServiceSlideout(event, "driver2_id");
    }

    imageCommentsOnClick(event: ClickEvent) {
        const image = event.target as Component;
        if (!image) return;

        const tableRow = TableRow.getContainingTableRow(image);
        if (tableRow == null) return;

        const modelRow = tableRow.data as ModelRow;
        EditEquipment.showSlideout({
            driverName: modelRow.get("driver1_name"),
            driverId: modelRow.get("driver1_id"),
            driver2Name: modelRow.get("driver2_name"),
            driver2Id: modelRow.get("driver2_id"),
            tractorId: modelRow.get("tractor"),
            trailerId: modelRow.get("trailer"),
            trailer2Id: modelRow.get("trailer2"),
            trailer3Id: modelRow.get("trailer3"),
            orderId: modelRow.get("order_id")
        });
    }

    private setReeferIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imageReefer = tableRow.findComponentById("imageReefer") as Image;
        PlanningUtil.setReeferIcon(imageReefer, modelRow);
    }

    /** This is an event handler for the onClick event of labelNextStopLateStatus. */
    async labelNextStopLateStatusOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data as ModelRow;
        let moveId: string;
        if (modelRow.get("move_status") === "D") {
            moveId = modelRow.get("np_move_id");
        } else {
            moveId = modelRow.get("movement_id");
        }
        const response = await Api.search("lme/dispatch/planning/late-route", {
            "movement_id": moveId,
            "is_current": "Y"
        });
        new LateRouteViewSlideout({
            movementId: moveId,
            stopId: modelRow.get("next_stop_stop_id"),
            isCurrent: response.actual_row_count > 0
        });
    }

    async labelNextStopLateStatusChevronOnClick(event: ClickEvent) {
        try {
            const tableRow = TableRow.getContainingTableRow(event.target as Component);
            const modelRow = tableRow.data as ModelRow;
            const panel = tableRow.findComponentById("panelNextStopLateStatus") as Panel;
            const stopId = modelRow.get("next_stop_stop_id");
            let moveId: string;
            if (modelRow.get("move_status") === "D") {
                moveId = modelRow.get("np_move_id");
            } else {
                moveId = modelRow.get("movement_id");
            }
            await PlanningUtil.labelNextStopLateStatusChevronOnClick(event, panel, stopId, moveId);
        } catch (error) {
            log.error("Error in labelNextStopLateStatusChevronOnClick:", error);
        }
    }

    openStatusSlideout(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + id,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.sourceEdistatus.search({order_id: id});
                    layout.sourceEdiOrder.search({order_id: id, movement_id: moveId});
                    layout.sourceEdiMovementCarrier.search({movement_id: moveId});
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const modelRows = layout.outboundDataSource.data;
                            modelRows?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                    layout.sourceEdistatus.search({order_id: id});
                                }
                            });
                        }
                    });
                },
                doAfterSlideOut: (decorator: SlideoutDecorator) => this.search()
            });
            layout.orderId = id;
            layout.movementId = moveId;
            layout.customerId = customerId;
        }
    }

    buttonMcMessagesOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Button);
        const modelRow = tableRow.data as ModelRow;
        MobileCommMessages.openSlideout({
            movementId: modelRow.get("movement_id"),
            tractorId: modelRow.get("tractor")
        });
    }

    manifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    detentionOnClick(event: ClickEvent) {
        const modelRow = TableRow.getContainingTableRow(event.target as Component)?.data as ModelRow;
        let orderId = null;
        if (modelRow && !modelRow.isNull("next_stop_order_id")) {
            orderId = modelRow.get("next_stop_order_id");
        }
        PlanningUtil.showMultiStopSlideout(event.target as Image, true, orderId);
    }
}

MixinUtil.applyMixin(OrderPlanningEquipment, OrderPlanningLayout);

// TypeScript interface declaration for mixin integration
export interface OrderPlanningEquipment extends OrderPlanningLayout {
}
